import { type ReactElement, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarRange, ClipboardList, FileSignature, School, Truck } from 'lucide-react';

import { useMeusTermos } from '@/modulos/portal-credenciado/contratos/dados';
import { numeroExibido } from '@/modulos/portal-credenciado/contratos/contrato';
import { DetailHeader } from '@/ui/DetailHeader';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import {
  ROTULO_DO_RECURSO,
  ROTULO_DO_STATUS,
  VARIANTE_DO_STATUS,
  dataLegivel,
  numeroDaOs,
  rotuloDaModalidade,
  type OrdemDoCredenciado,
} from './contrato';
import { TEXTO_SEM_TERMO_ATIVO, useOrdemDoCredenciado } from './dados';

const CAMINHO_DOS_TERMOS = '/credenciado/contratos';

const CLASSE_DO_LINK =
  'text-link focus-visible:ring-ring rounded font-medium hover:underline focus:outline-hidden focus-visible:ring-2';

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }): ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{rotulo}</dt>
      <dd className="wrap-break-word text-sm font-medium">{children ?? '—'}</dd>
    </div>
  );
}

function Cartao({
  titulo,
  icone: Icone,
  children,
}: {
  titulo: string;
  icone: typeof CalendarRange;
  children: ReactNode;
}): ReactElement {
  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icone className="h-4 w-4" aria-hidden="true" />
          {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** O Termo da OS, com o número quando "Meus termos" já o trouxe. */
function LinkDoTermo({ termoId }: { termoId: string }): ReactElement {
  const termo = useMeusTermos().data?.find((t) => t.id === termoId);
  return (
    <Link
      to={`${CAMINHO_DOS_TERMOS}/${encodeURIComponent(termoId)}?aba=os`}
      className={CLASSE_DO_LINK}
    >
      {termo ? `Termo de Adesão nº ${numeroExibido(termo)}` : 'Abrir o Termo de Adesão'}
    </Link>
  );
}

function Conteudo({ os }: { os: OrdemDoCredenciado }): ReactElement {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Cartao titulo="Vigência" icone={CalendarRange}>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Início">{dataLegivel(os.inicio)}</Campo>
          <Campo rotulo="Fim">{dataLegivel(os.fim)}</Campo>
        </dl>
      </Cartao>

      <Cartao titulo="Recursos" icone={Truck}>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo={`${ROTULO_DO_RECURSO.VEICULO} (placa)`}>
            {os.placa && <span className="font-mono">{os.placa}</span>}
          </Campo>
          <Campo rotulo="CRM do veículo">
            {os.crm && <span className="font-mono">{os.crm}</span>}
          </Campo>
          <Campo rotulo={ROTULO_DO_RECURSO.CONDUTOR}>{os.condutor}</Campo>
          <Campo rotulo={ROTULO_DO_RECURSO.MONITOR}>{os.monitor}</Campo>
        </dl>
      </Cartao>

      <Cartao titulo="Atendimento" icone={School}>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="DRE">{os.dre && `${os.dre.codigo} — ${os.dre.nome}`}</Campo>
          <Campo rotulo="Modalidade">{rotuloDaModalidade(os.modalidade)}</Campo>
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-muted-foreground text-xs uppercase tracking-wide">
              Escolas atendidas
            </dt>
            <dd className="text-sm font-medium">
              {os.escolas.length === 0 ? (
                '—'
              ) : (
                <ul className="mt-1 space-y-1">
                  {os.escolas.map((escola) => (
                    <li key={escola.codigoEol} className="wrap-break-word">
                      {escola.nome}{' '}
                      <span className="text-muted-foreground font-normal">
                        (EOL {escola.codigoEol})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      </Cartao>

      <Cartao titulo="Termo de Adesão" icone={FileSignature}>
        <dl className="grid gap-3">
          <Campo rotulo="Emitida sob o termo">
            {os.termoId && <LinkDoTermo termoId={os.termoId} />}
          </Campo>
        </dl>
      </Cartao>
    </div>
  );
}

/**
 * `/credenciado/ordens-de-servico/:id` — o detalhe de uma Ordem de Serviço do
 * credenciado, aberto pela linha da sub-tabela de Contratos e Termos ou da aba
 * "OS vinculadas" do Termo (regra 9). Só leitura, da projeção real J-06/S03:
 * a API não tem detalhe por id, então a OS é procurada na mesma coleção
 * (`useOrdemDoCredenciado`). Sem `ativacaoId`, revisão nem identificador de
 * correlação na tela: são da trilha do DTP (regra 8).
 */
export function OrdemServicoDetailPage(): ReactElement {
  const { id } = useParams();
  const consulta = useOrdemDoCredenciado(id);
  const os = consulta.dados;

  let corpo: ReactElement;
  if (consulta.semTermoAtivo) {
    corpo = (
      <EmptyState
        icon={ClipboardList}
        title="Sem Ordem de Serviço"
        description={TEXTO_SEM_TERMO_ATIVO}
      />
    );
  } else if (os === null) {
    corpo = (
      <ErrorState
        variant="resource-not-found"
        title="OS não encontrada"
        description="Confira as Ordens de Serviço em Contratos e Termos."
        primaryAction={{ label: 'Ir para Contratos e Termos', to: CAMINHO_DOS_TERMOS }}
      />
    );
  } else {
    corpo = (
      <EstadoDaTela
        carregando={consulta.carregando}
        erro={consulta.erro}
        aoTentarDeNovo={consulta.tentarDeNovo}
        descricao="a Ordem de Serviço"
      >
        {os && <Conteudo os={os} />}
      </EstadoDaTela>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      <DetailHeader
        backTo={CAMINHO_DOS_TERMOS}
        backLabel="Contratos e Termos"
        title={os ? numeroDaOs(os) : undefined}
        subtitle={os ? 'Ordem de Serviço' : undefined}
        status={
          os
            ? { variant: VARIANTE_DO_STATUS[os.status], label: ROTULO_DO_STATUS[os.status] }
            : undefined
        }
      />
      {corpo}
    </div>
  );
}

export default OrdemServicoDetailPage;
